import { Outlet, useLocation, useOutletContext } from 'react-router-dom'

import { Alert, Button, useToggle } from '@factorialco/deprecated-design-system'
import { StandardLayout } from '@factorialco/f0-react'
import {
  ProfilePageDocument,
  UpdateAccessDocument,
  UpdateEmployeeDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { WhatsAppButton } from 'modules/core/components/WhatsAppButton'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { usePrompt } from 'modules/core/lib/react-router'

import Actions from 'modules/employees/components/Actions'
import EmployeeStatusBanner from 'modules/employees/components/blocks/EmployeeStatusBanner'
import WorkForm from 'modules/employees/lib/forms/EmployeeForm/WorkInformation'
import UserForm from 'modules/employees/lib/forms/Personal/User'
import Header from 'modules/layout/components/Page/Header'
import ConfirmPasswordModal from 'modules/profile/components/ConfirmPasswordModal'
import ManagerTogglerModal from 'modules/profile/components/ManagerTogglerModal'
import ProfileForm from 'modules/profile/components/ProfileForm'
import { ProfileEmployeeData } from 'modules/profile/components/ProfileForm/types'
import EmailForm from 'modules/profile/lib/forms/Email'
import PersonalEmailForm from 'modules/profile/lib/forms/PersonalEmail'
import LoadingState from 'modules/profile/pages/LoadingState'

import { RouteContext } from './RootPage'

function createEmailForm(shareProfileEmail: boolean, loginEmail: string) {
  return new EmailForm({
    email: shareProfileEmail ? loginEmail : null,
  })
}

function createPersonalEmailForm(shareProfileEmail: boolean, personalEmail: string) {
  return new PersonalEmailForm({
    personal_email: shareProfileEmail ? personalEmail : null,
  })
}

function createWorkForm(
  managerId: number,
  locationId: number,
  employeeGroupId: number,
  timeoffManagerId: number,
  payrollable: boolean,
  companyIdentifier: string,
  seniorityCalculationDate: string,
  shareBirthday: boolean
) {
  return new WorkForm({
    manager_id: managerId,
    location_id: locationId,
    employee_group_id: employeeGroupId,
    timeoff_manager_id: timeoffManagerId,
    payrollable: payrollable,
    company_identifier: companyIdentifier,
    seniority_calculation_date: seniorityCalculationDate,
    show_birthday: shareBirthday,
  })
}

function createUserForm(employeeData?: ProfileEmployeeData) {
  if (!employeeData) {
    return new UserForm({
      avatar: null,
      avatar_full: null,
      first_name: '',
      last_name: '',
      communications_email: '',
      remove_avatar: false,
      unconfirmed_communications_email: '',
      all_tenants: false,
    })
  }

  const fullName = employeeData?.access?.fullName || ''
  const nameParts = fullName.split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  return new UserForm({
    avatar: employeeData?.access?.avatar || null,
    avatar_full: employeeData?.access?.avatarFull || null,
    first_name: firstName,
    last_name: lastName,
    communications_email: employeeData?.access?.email || employeeData?.loginEmail || '',
    remove_avatar: false,
    unconfirmed_communications_email: employeeData?.unconfirmedCommunicationsEmail || '',
    all_tenants: false, // Is this legacy? Do we still need this here?
  })
}

const Profile = observer(() => {
  const { pathname } = useLocation()

  useEffect(() => {
    track('View employee profile', {
      page_path: pathname,
      page_url: window.location.href,
      layout_version: 'v1',
    })
  }, [pathname])

  const context = useOutletContext<RouteContext>()
  const {
    canSeePermissions,
    canSeePersonalInfo,
    canSeeCustomDimensions,
    cardEmployeeRight,
    permissionGroupName,
    permissionGroupId,
    headerTitle,
    seeOldCostCentersAssignments,
  } = context
  const [isManagerTogglerOpen, setManagerTogglerOpen] = useToggle()
  const [passwordModalOpen, setPasswordModalOpen] = useToggle()
  const [changeEmailOAuthModalOpen, setChangeEmailOAuthModalOpen] = useToggle()

  const fetchAuthInformation = async () => {
    const api = ApiClient()

    return api.get('/cognito/users/current')
  }

  const changeUserEmail = async (accessId: number, email: string, submittedPassword: string) => {
    const api = ApiClient()

    return api.post(`/accesses/${accessId}/change_email`, {
      email: email,
      submitted_password: submittedPassword,
    })
  }

  const { id: routeId } = useStrictParams<'id'>()
  const {
    email: currentEmail,
    employee: currentEmployee,
    company: { id: companyId },
  } = useCurrentSession()
  const employeeId = routeId ?? currentEmployee.id

  const {
    data,
    loading,
    refetch: refetchProfilePage,
  } = useQuery(ProfilePageDocument, {
    variables: {
      companyId,
      employeeId: Number(employeeId),
      usageGroupSlugs: ['employees-questions', 'employee-profile'],
    },
  })
  const employeeData = data?.employees.employeesConnection.edges?.[0]?.node

  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (mutationData) => mutationData.employeesMutations.updateEmployeesEmployee,
  })

  const [updateAccess] = useMutation(UpdateAccessDocument, {
    extractorFn: (mutationData) => mutationData.apiCoreMutations.updateApicoreAccess,
  })

  const [workForm, emailForm, personalEmailForm, userForm] = useUnsafeMemo(
    () => [
      createWorkForm(
        employeeData?.manager?.id ?? 0,
        employeeData?.locationId ?? 0,
        employeeData?.employeeGroup?.id ?? 0,
        employeeData?.timeoffManager?.id ?? 0,
        employeeData?.payrollable ?? false,
        employeeData?.companyIdentifier ?? '',
        employeeData?.seniorityCalculationDate ?? '',
        employeeData?.shareBirthday ?? false
      ),
      createEmailForm(
        employeeData?._policies?.shareProfileEmail.can ?? false,
        employeeData?.email ?? ''
      ),
      createPersonalEmailForm(
        employeeData?._policies?.shareProfileEmail.can ?? false,
        employeeData?.personalEmail ?? ''
      ),
      createUserForm(employeeData),
    ],
    [employeeData]
  )

  usePrompt(
    i18n.t('common.confirm_navigation'),
    () => workForm.isDirty || emailForm.isDirty || personalEmailForm.isDirty
  )

  const userIsInvited = !!employeeData?.access?.invitedOn
  const userIsSelf = currentEmail !== employeeData?.access.email
  const onClosePasswordModal = setPasswordModalOpen(false)
  const onCloseChangeEmailOAuthModal = setChangeEmailOAuthModalOpen(false)
  const onCloseManagerModal = setManagerTogglerOpen(false)

  const getSuccessMessage = () => {
    if (userIsSelf || !emailForm.isDirty || !personalEmailForm.isDirty || !userIsInvited) {
      return undefined
    }

    return i18n.t('common.confirm_password_success', {
      email: emailForm.get('email').value,
    })
  }

  const { running, performAction } = useAsyncAction(
    async () => {
      //if (!employeeData) throw new Error('Employee data not available')

      const promises: Promise<unknown>[] = []

      if ((workForm.isDirty || personalEmailForm.isDirty) && employeeData?.id) {
        const { seniority_calculation_date: seniorityCalculationDate, ...workFormData } =
          workForm.data()

        const updateEmployeeVars = {
          id: employeeData.id,
          accessId: employeeData.access?.id || 0,
          managerId: workFormData.manager_id || null,
          locationId: workFormData.location_id || null,
          employeeGroupId: workFormData.employee_group_id || null,
          timeoffManagerId: workFormData.timeoff_manager_id || null,
          showBirthday: workFormData.show_birthday,
          ...(workForm.get('company_identifier').isDirty && {
            companyIdentifier: workFormData.company_identifier,
          }),
          ...(workForm.dirtyFieldsKeys.includes('seniority_calculation_date') && {
            seniorityCalculationDate,
          }),
          ...(personalEmailForm.isDirty && {
            personalEmail: personalEmailForm.data().personal_email,
          }),
        }

        promises.push(updateEmployee({ variables: updateEmployeeVars }))
      }

      if (userForm.isDirty && employeeData?._policies?.editPersonalInfo?.can) {
        const userFormData = userForm.data()
        const updateAccessVars = {
          id: employeeData.access?.id || 0,
          firstName: userFormData.first_name,
          lastName: userFormData.last_name,
          ...(userFormData.avatar && { avatar: userFormData.avatar }),
          ...(userFormData.remove_avatar && { removeAvatar: true }),
        }

        promises.push(updateAccess({ variables: updateAccessVars }))
      }

      if (emailForm.isDirty) {
        promises.push(
          changeUserEmail(
            employeeData?.access?.id || 0,
            emailForm.get('email').value,
            emailForm.get('submitted_password').value
          )
        )
      }

      await Promise.all(promises)

      // Reset forms after successful save
      if (emailForm.isDirty) {
        emailForm.setValues({
          email: employeeData?.access?.email || '',
          unconfirmed_email: '',
          password: '',
          submitted_password: '',
        })
        emailForm.cleanAll()
      }

      if (personalEmailForm.isDirty) {
        personalEmailForm.cleanAll()
      }

      refetchProfilePage()

      if (workForm.isDirty) {
        workForm.cleanAll()
      }

      if (userForm.isDirty) {
        userForm.cleanAll()
      }

      onClosePasswordModal()
      onCloseChangeEmailOAuthModal()
      onCloseManagerModal()
    },
    {
      successMessage: getSuccessMessage(),
    }
  )

  const onSubmit = async (): Promise<void> => {
    if (
      !isManagerTogglerOpen &&
      employeeData?._policies?.editPersonalInfo?.can &&
      workForm.get('manager_id').isDirty &&
      workForm.get('manager_id').value &&
      workForm.get('manager_id').value !== +workForm.get('timeoff_manager_id').value
    ) {
      return setManagerTogglerOpen(true)()
    }

    if (emailForm.isDirty) {
      const { success, value } = await asyncAction(() => fetchAuthInformation(), {
        successFlash: false,
      })

      if (!success) return

      if (value?.oauth_login) {
        return setChangeEmailOAuthModalOpen(true)()
      }

      return setPasswordModalOpen(true)()
    }
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(workForm),
      legal_entity_country: employeeData?.legalEntity?.country?.toLowerCase() || '',
      layout_version: 'v1',
    })
    performAction()
  }

  const onDeleteAvatar = () => {
    userForm.setValues({
      avatar: null,
      remove_avatar: true,
    })
  }

  const customResourceFieldsData = compact(
    data?.customFields?.resourceFields.filter((resourceField) => !!resourceField?.field) ?? []
  )
  const customValuesData = data?.employees?.employeesConnection?.edges?.[0]?.node?.values ?? []
  const companyTeams = data?.teams?.teams ?? []
  const companyLocations = data?.locations?.locations ?? []
  const onboardingProcess =
    data?.employeeOnboarding?.employeeOnboardingsConnection?.nodes[0]?.materializedProcess?.process
  const features = data?.features?.featuresConnection?.nodes ?? []
  const hasWhatsappFeature =
    features.find((feature) => feature.name === 'employee_chat_on_whatsapp')?.enabled ?? false
  const canSeeTeams = data?.teams?._policies?.companySeeTeams?.can ?? false
  const canManageTeams = data?.teams?._policies?.teamManage?.can ?? false
  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes ?? []
  const moduleAvailabilities = data?.companies?.moduleAvailabilitiesConnection?.nodes ?? []

  if (loading) {
    return (
      <StandardLayout variant="narrow">
        <LoadingState />
      </StandardLayout>
    )
  }

  return (
    employeeData && (
      <StandardLayout variant="narrow">
        <PortalWrapper container={cardEmployeeRight}>
          <WhatsAppButton
            employee={employeeData}
            canSeePersonalInfo={canSeePersonalInfo}
            hasWhatsappFeature={hasWhatsappFeature}
          />
          <Actions employeeData={employeeData} />
        </PortalWrapper>
        <PortalWrapper container={headerTitle}>
          <Header.Title>{i18n.t('profile.work_details')}</Header.Title>
        </PortalWrapper>

        <EmployeeStatusBanner
          access={employeeData.access}
          contractStartDate={employeeData.referenceContract?.startsOn}
          employee={employeeData}
          policies={employeeData._policies}
          onboardingProcessName={onboardingProcess?.name}
        />

        <ProfileForm
          currentEmployee={currentEmployee}
          employeeData={employeeData}
          onSubmitWorkForm={onSubmit}
          emailForm={emailForm}
          personalEmailForm={personalEmailForm}
          workForm={workForm}
          userForm={userForm}
          onDeleteAvatar={onDeleteAvatar}
          permissionGroupName={permissionGroupName}
          permissionGroupId={permissionGroupId}
          seeOldCostCentersAssignments={seeOldCostCentersAssignments}
          canSeeCustomDimensions={canSeeCustomDimensions}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          companyTeams={companyTeams}
          moduleAvailabilities={moduleAvailabilities}
          featureAvailabilities={featureAvailabilities}
          features={features}
          companyLocations={companyLocations}
          canSeeTeams={canSeeTeams}
          canManageTeams={canManageTeams}
          canSeePersonalInfo={canSeePersonalInfo}
          canSeePermissions={canSeePermissions}
        />
        {isManagerTogglerOpen && (
          <ManagerTogglerModal
            employeeName={employeeData.access.fullName ?? ''}
            workForm={workForm}
            running={running}
            onClose={onCloseManagerModal}
            onSubmit={onSubmit}
          />
        )}
        {passwordModalOpen && (
          <ConfirmPasswordModal
            employeeIsOwn={userIsSelf}
            field={emailForm.get('submitted_password')}
            running={running}
            onClose={onClosePasswordModal}
            onSubmit={performAction}
          />
        )}
        {changeEmailOAuthModalOpen && (
          <Modal
            illustration="alert"
            title={i18n.t('common.confirm_email_change.title')}
            description={i18n.t('common.confirm_email_change.description')}
            onClose={onCloseChangeEmailOAuthModal}
            footer={
              <Button onClick={performAction} disabled={running}>
                {i18n.t('common.confirm_email_change.submit')}
              </Button>
            }
          >
            <Alert color="warning">{i18n.t('common.confirm_email_change.alert')}</Alert>
          </Modal>
        )}
        <Outlet context={context} />
      </StandardLayout>
    )
  )
})

export default Profile
